import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { base_url } from "../../Config/Constant";

const Training = () => {
  const { clickid } = useParams();
  const [trainingInfo, setTrainingInfo] = useState({});
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTrainingInfo = async () => {
      try {
        const response = await axios.get(`${base_url}/track/training/${clickid}`);
        const data = response.data.data || response.data;
        setTrainingInfo(data);
        if (data.trainingViewed) {
          setCompleted(true);
        }
        setLoading(false);
      } catch (err) {
        window.location.replace("https://smishtrap.com/awareness");
      }
    };
    fetchTrainingInfo();
  }, [clickid]);

  const handleComplete = async () => {
    try {
      await axios.post(`${base_url}/track/training-complete/${clickid}`);
      setCompleted(true);
    } catch (err) {
      setError("Unable to complete training. Please try again.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500 text-lg">Loading...</p>
      </div>
    );
  }

  if (error && !trainingInfo.campaignName) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-red-500 text-lg">{error}</p>
      </div>
    );
  }

  return (
    <div className="training-page min-h-screen bg-gray-50 flex flex-col items-center py-12 px-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-lg p-8">

        {/* Warning header */}
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">&#x1f6e1;&#xfe0f;</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            This Was a Security Awareness Test
          </h1>
          <p className="text-gray-500 text-lg">
            Your organization conducted an authorized phishing simulation
          </p>
        </div>

        <hr className="mb-6" />

        {/* What happened section */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-700 mb-3">What Happened</h2>
          <p className="text-gray-600">
            You received a simulated smishing (SMS phishing) message as part of a security
            awareness exercise. You clicked a link and entered credentials on a simulated
            login page. In a real attack, this information could have been stolen.
          </p>
          {/* Show the original message template if available */}
          {trainingInfo.templateMessage && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">The message you received:</p>
              <p className="text-sm text-red-600 mt-1">{trainingInfo.templateMessage}</p>
            </div>
          )}
        </div>

        {/* Red flags section */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-700 mb-3">Red Flags to Watch For</h2>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1">&#x26a0;&#xfe0f;</span>
              <span className="text-gray-600">Unsolicited messages containing links</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1">&#x26a0;&#xfe0f;</span>
              <span className="text-gray-600">Urgency or pressure to act immediately</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1">&#x26a0;&#xfe0f;</span>
              <span className="text-gray-600">Requests for personal information or credentials</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1">&#x26a0;&#xfe0f;</span>
              <span className="text-gray-600">Unfamiliar or suspicious sender numbers</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-red-500 mt-1">&#x26a0;&#xfe0f;</span>
              <span className="text-gray-600">Login pages that don't match your organization's official URLs</span>
            </li>
          </ul>
        </div>

        {/* Tips section */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-700 mb-3">How to Stay Safe</h2>
          <ul className="space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">&#x2705;</span>
              <span className="text-gray-600">Never click links in unexpected text messages</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">&#x2705;</span>
              <span className="text-gray-600">Verify requests by contacting the sender through official channels</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">&#x2705;</span>
              <span className="text-gray-600">Use multi-factor authentication (MFA) on all accounts</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">&#x2705;</span>
              <span className="text-gray-600">Report suspicious messages to your IT security team</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500 mt-1">&#x2705;</span>
              <span className="text-gray-600">When in doubt, navigate directly to the website instead of clicking a link</span>
            </li>
          </ul>
        </div>

        {/* Complete training button */}
        {!completed ? (
          <button
            onClick={handleComplete}
            className="w-full py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition"
          >
            Complete Training
          </button>
        ) : (
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-green-700 font-semibold">Training Complete — Thank you!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Training;
